import React, { useRef, useState, useLayoutEffect } from 'react';
import TransformPoints from './TransformPoints';
import Konva from 'konva';
import { WellIngredientDropInfoDetails } from 'components/Experiment/constants';
import { Rect, Text, Group } from 'react-konva';
import { RoundValue } from 'utils/Global';
import { WellDropInfoPanelTooltip } from './WellDropInfoPanelTooltip';

const measureTextWidth = (
  text: string,
  fontSize: number,
  fontFamily: string = 'Arial',
): number => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  context.font = `${fontSize}px ${fontFamily}`;
  return context.measureText(text).width;
};

const truncateMiddle = (
  text: string,
  maxWidth: number,
  fontSize: number,
): { text: string; isTruncated: boolean } => {
  const fullWidth = measureTextWidth(text, fontSize);
  if (fullWidth <= maxWidth) {
    return { text, isTruncated: false };
  }

  const ellipsis = '...';
  const ellipsisWidth = measureTextWidth(ellipsis, fontSize);
  const availableWidth = maxWidth - ellipsisWidth;

  let leftLen = 0;
  let rightLen = 0;

  for (let i = 1; i <= text.length; i++) {
    const testText = text.substring(0, i);
    if (measureTextWidth(testText, fontSize) <= availableWidth / 2) {
      leftLen = i;
    } else {
      break;
    }
  }

  for (let i = 1; i <= text.length - leftLen; i++) {
    const rightPart = text.substring(text.length - i);
    const testText = text.substring(0, leftLen) + ellipsis + rightPart;
    if (measureTextWidth(testText, fontSize) <= maxWidth) {
      rightLen = i;
    } else {
      break;
    }
  }

  if (leftLen === 0 || rightLen === 0) {
    leftLen = Math.floor((text.length - 3) / 2);
    rightLen = text.length - leftLen - 3;

    let testText =
      text.substring(0, leftLen) + ellipsis + text.substring(text.length - rightLen);
    while (
      measureTextWidth(testText, fontSize) > maxWidth &&
      (leftLen > 0 || rightLen > 0)
    ) {
      if (leftLen > rightLen) {
        leftLen--;
      } else {
        rightLen--;
      }
      testText =
        text.substring(0, leftLen) + ellipsis + text.substring(text.length - rightLen);
    }
  }

  const truncated =
    text.substring(0, leftLen) + ellipsis + text.substring(text.length - rightLen);
  return { text: truncated, isTruncated: true };
};

const truncateEnd = (
  text: string,
  maxWidth: number,
  fontSize: number,
): { text: string; isTruncated: boolean } => {
  const fullWidth = measureTextWidth(text, fontSize);
  if (fullWidth <= maxWidth) {
    return { text, isTruncated: false };
  }

  const ellipsis = '...';
  let truncated = text;

  let start = 0;
  let end = text.length;

  while (start < end) {
    const mid = Math.floor((start + end) / 2);
    const testText = text.substring(0, mid) + ellipsis;
    const testWidth = measureTextWidth(testText, fontSize);

    if (testWidth <= maxWidth) {
      truncated = testText;
      start = mid + 1;
    } else {
      end = mid;
    }
  }

  return { text: truncated, isTruncated: true };
};

export type WellDropInfoPanelProps = {
  wellIngDropInfo: WellIngredientDropInfoDetails;
  tpObject: TransformPoints;
};

const WellDropInfoPanel = (props: WellDropInfoPanelProps) => {
  const { wellIngDropInfo, tpObject } = props;
  const {
    plateBarcode,
    wellName,
    dropNumber,
    dropWellVolume,
    dropProteinVolume,
    wellIngredients,
  } = wellIngDropInfo || {};

  const ingredientsTextRef = useRef<any>(null);
  const groupRef = useRef<Konva.Group>(null);

  const [innerRectHeight, setInnerRectHeight] = useState<number>(50);
  const [hover, setHover] = useState<boolean>(false);

  const wellInfoData = {
    rectWidth: 240,
    cornerRadius: 4,
    rectFill: '#FFFFFF',
    padding: 15,
    fontSizeHeading: 24,
    fontSizeText: 12,
    textColorHeading: '#000000',
    textColorText: '#222222',
  };

  const textAvailableWidth = wellInfoData.rectWidth - wellInfoData.padding * 2;

  const fullHeading = `${plateBarcode}, ${wellName}, ${dropNumber}`;
  const headingTruncated = truncateMiddle(
    fullHeading,
    textAvailableWidth,
    wellInfoData.fontSizeHeading,
  );

  const dropWellVolText = `Drop Well Vol: ${RoundValue(dropWellVolume)} μL`;
  const dropWellVolTruncated = truncateEnd(
    dropWellVolText,
    textAvailableWidth,
    wellInfoData.fontSizeText,
  );

  const dropProteinVolText = `Drop Protein Vol: ${RoundValue(dropProteinVolume)} μL`;
  const dropProteinVolTruncated = truncateEnd(
    dropProteinVolText,
    textAvailableWidth,
    wellInfoData.fontSizeText,
  );

  const dropInfo = `${dropWellVolTruncated.text}\n${dropProteinVolTruncated.text}`;

  const tooltipLines: string[] = [];
  if (
    headingTruncated.isTruncated ||
    dropWellVolTruncated.isTruncated ||
    dropProteinVolTruncated.isTruncated
  ) {
    tooltipLines.push(`Barcode : ${plateBarcode}`);
    tooltipLines.push(`Drop Well Vol : ${RoundValue(dropWellVolume)} μL`);
    tooltipLines.push(`Drop Protein Vol : ${RoundValue(dropProteinVolume)} μL`);
  }
  const tooltipText = tooltipLines.join('\n');
  const showTooltip = hover && tooltipLines.length > 0;

  const wellIngs = wellIngredients
    .map(
      (w) =>
        `${RoundValue(w.conc)} ${w.concUnit} ${w.shortName}${
          w.pH ? ' ' + RoundValue(w.pH) + ' pH' : ''
        } (${w.type})`,
    )
    .join('\n');

  const rectScale = 0.25;
  const scale = (tpObject.scalingWidth * rectScale) / wellInfoData.rectWidth;

  const headingHeight = wellInfoData.fontSizeHeading * 1.4;
  const dropLines = dropInfo.split('\n').length;
  const dropInfoHeight = wellInfoData.fontSizeText * dropLines * 1.1;
  const totalHeight = wellInfoData.padding + headingHeight + innerRectHeight + 10;

  useLayoutEffect(() => {
    groupRef.current?.moveToTop();
    if (ingredientsTextRef.current) {
      const ingHeight = ingredientsTextRef.current.height();
      setInnerRectHeight(ingHeight + dropInfoHeight + wellInfoData.padding * 2);
    }
  }, [wellIngs, dropInfoHeight]);

  const groupX =
    tpObject.leftMargin +
    tpObject.scalingWidth -
    (wellInfoData.rectWidth * scale + wellInfoData.padding);
  const groupY = tpObject.topMargin + wellInfoData.padding;

  return (
    <Group x={groupX} y={groupY} scale={{ x: scale, y: scale }}>
      <Group
        data-testid="group-well-drop-info"
        ref={groupRef}
        clipX={0}
        clipY={0}
        clipWidth={wellInfoData.rectWidth}
        clipHeight={totalHeight}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <Rect
          data-testid="rect-well-drop-info"
          width={wellInfoData.rectWidth}
          height={totalHeight}
          cornerRadius={wellInfoData.cornerRadius}
          fill={wellInfoData.rectFill}
          shadowColor="rgba(0, 0, 0, 0.2)"
          shadowBlur={10}
          shadowOffsetX={2}
          shadowOffsetY={2}
          opacity={hover ? 1.0 : 0.7}
          stroke="#CACACA"
          strokeWidth={1}
        />

        <Text
          data-testid="text-well-drop-info-heading"
          x={wellInfoData.padding}
          y={wellInfoData.padding}
          text={headingTruncated.text}
          fontSize={wellInfoData.fontSizeHeading}
          fill={wellInfoData.textColorHeading}
          width={wellInfoData.rectWidth - wellInfoData.padding * 2}
          align="right"
        />

        <Rect
          data-testid="rect-well-drop-info-ings"
          x={wellInfoData.padding - 6}
          y={wellInfoData.padding + headingHeight}
          width={wellInfoData.rectWidth - wellInfoData.padding * 2 + 12}
          height={innerRectHeight}
          fill="rgba(0,0,0,0.06)"
          cornerRadius={8}
        />

        <Text
          data-testid="text-well-drop-info-drop-well-vol"
          x={wellInfoData.padding}
          y={wellInfoData.padding + headingHeight + 10}
          text={dropWellVolTruncated.text}
          fontSize={wellInfoData.fontSizeText}
          fill={wellInfoData.textColorText}
          width={wellInfoData.rectWidth - wellInfoData.padding * 2}
          align="right"
          lineHeight={1.1}
        />

        <Text
          data-testid="text-well-drop-info-drop-protein-vol"
          x={wellInfoData.padding}
          y={wellInfoData.padding + headingHeight + 10 + wellInfoData.fontSizeText * 1.1}
          text={dropProteinVolTruncated.text}
          fontSize={wellInfoData.fontSizeText}
          fill={wellInfoData.textColorText}
          width={wellInfoData.rectWidth - wellInfoData.padding * 2}
          align="right"
          lineHeight={1.1}
        />

        <Text
          data-testid="text-well-drop-info-well-ings"
          ref={ingredientsTextRef}
          x={wellInfoData.padding}
          y={wellInfoData.padding + headingHeight + dropInfoHeight + 20}
          text={wellIngs}
          fontSize={wellInfoData.fontSizeText}
          fill={wellInfoData.textColorText}
          width={wellInfoData.rectWidth - wellInfoData.padding * 2}
          align="right"
          lineHeight={1.2}
        />
      </Group>

      {showTooltip && (
        <WellDropInfoPanelTooltip
          text={tooltipText}
          x={wellInfoData.rectWidth}
          y={totalHeight}
          visible={true}
          alignRight={true}
        />
      )}
    </Group>
  );
};

export default WellDropInfoPanel;
